import pdfMake from 'pdfmake/build/pdfmake';

// Import fonts using require to avoid webpack issues
const pdfFonts = require('pdfmake/build/vfs_fonts');

// Set up fonts
pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs;

/**
 * Generates a professional baseline reconciliation report using pdfmake
 */
export function generateBaselineReport(options) {
    const {
        clientName,
        categorizedResults,
        exportAllResults,
        baselineType,
        user,
        logoFile,
        supportMessage,
        categoryIcons = {}
    } = options;

    // Calculate summary statistics from ALL results (not just filtered)
    const fullStats = calculateStatistics(categorizedResults);

    // For display, we might be showing filtered results
    const displayResults = categorizedResults;

    const docDefinition = {
        pageSize: 'A4',
        pageMargins: [50, 60, 50, 70],

        // Enhanced header
        header: (currentPage) => {
            if (currentPage === 1) return null;
            return {
                margin: [50, 25, 50, 0],
                table: {
                    widths: ['*', 'auto'],
                    body: [[
                        {
                            text: `${clientName} - ${baselineType} Report`,
                            fontSize: 9,
                            color: '#64748b',
                            bold: false,
                            border: [false, false, false, true],
                            borderColor: ['', '', '', '#e2e8f0']
                        },
                        {
                            text: new Date().toLocaleDateString('en-GB'),
                            fontSize: 9,
                            color: '#64748b',
                            alignment: 'right',
                            border: [false, false, false, true],
                            borderColor: ['', '', '', '#e2e8f0']
                        }
                    ]]
                }
            };
        },

        // Enhanced footer
        footer: (currentPage, pageCount) => {
            return {
                margin: [50, 15, 50, 0],
                columns: [
                    {
                        text: user ? `Generated by ${user.displayName || user.upn}` : '',
                        fontSize: 8,
                        color: '#94a3b8',
                        width: '*'
                    },
                    {
                        text: `Page ${currentPage} of ${pageCount}`,
                        alignment: 'right',
                        fontSize: 8,
                        color: '#94a3b8',
                        width: 'auto'
                    }
                ]
            };
        },

        content: [
            // Custom logo with professional spacing
            ...(logoFile ? [{
                image: logoFile,
                width: 495,
                alignment: 'center',
                margin: [0, 0, 0, 15]
            }] : []),

            // Enhanced title section
            {
                canvas: [
                    {
                        type: 'rect',
                        x: 0,
                        y: 0,
                        w: 495,
                        h: 80,
                        r: 8,
                        color: '#0f172a'
                    }
                ]
            },
            {
                stack: [
                    {
                        text: clientName,
                        fontSize: 28,
                        bold: true,
                        color: 'white',
                        alignment: 'center',
                        margin: [0, -75, 0, 0]
                    },
                    {
                        text: `${baselineType} - Reconciliation Report`,
                        fontSize: 14,
                        color: '#cbd5e1',
                        alignment: 'center',
                        margin: [0, 8, 0, 0]
                    },
                    {
                        text: `Generated: ${new Date().toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                        })}`,
                        fontSize: 10,
                        color: '#94a3b8',
                        alignment: 'center',
                        margin: [0, 0, 0, 10]
                    }
                ]
            },

            { text: '', margin: [0, 0, 0, logoFile ? 20 : 15] },

            // Baseline explanation
            ...(baselineType.includes('Standard') ? [{
                table: {
                    widths: ['*'],
                    body: [[
                        {
                            stack: [
                                {
                                    text: 'About This Report',
                                    fontSize: 11,
                                    bold: true,
                                    color: '#0f172a',
                                    margin: [0, 0, 0, 5]
                                },
                                {
                                    text: 'This report compares your tenant configuration against standard Microsoft 365 security best practices that apply to all organizations. Deviations from the baseline may indicate security risks or compliance gaps that require attention.',
                                    fontSize: 9,
                                    color: '#475569',
                                    lineHeight: 1.4
                                }
                            ],
                            fillColor: '#f1f5f9',
                            margin: 15,
                            border: [false, false, false, false]
                        }
                    ]]
                },
                margin: [0, 0, 0, 15]
            }] : []),

            // Executive Summary header
            {
                text: 'Executive Summary',
                fontSize: 20,
                bold: true,
                color: '#0f172a',
                margin: [0, 0, 0, 15],
            },

            // Enhanced statistics cards with gradient effect
            {
                columns: [
                    createEnhancedStatCard('Total Checks', fullStats.total, '#3b82f6', '#2563eb'),
                    createEnhancedStatCard('Passed', fullStats.passed, '#10b981', '#059669'),
                    createEnhancedStatCard('Failed', fullStats.failed, '#ef4444', '#dc2626'),
                    createEnhancedStatCard('Pass Rate', `${fullStats.passRate}%`, '#8b5cf6', '#7c3aed')
                ],
                columnGap: 15,
                margin: [0, 0, 0, 15]
            },

            // Status indicator
            ...(exportAllResults ? [] : [{
                table: {
                    widths: ['*'],
                    body: [[
                        {
                            text: [
                                { text: '⚠️  ', fontSize: 12 },
                                { text: 'Showing Failed Results Only', bold: true, fontSize: 10 },
                                { text: ' - This report displays only configuration items that do not meet the baseline requirements.', fontSize: 9 }
                            ],
                            fillColor: '#fef3c7',
                            color: '#92400e',
                            margin: 10,
                            border: [false, false, false, false]
                        }
                    ]]
                },
                margin: [0, 0, 0, 10]
            }]),

            // Category breakdown
            {
                text: 'Category Breakdown',
                fontSize: 16,
                bold: true,
                color: '#0f172a',
                margin: [0, 0, 0, 10]
            },

            createProfessionalCategoryTable(displayResults, categoryIcons),

            // Detailed Results
            {
                text: 'Detailed Findings',
                fontSize: 20,
                bold: true,
                color: '#0f172a',
                pageBreak: 'before',
                margin: [0, 0, 0, 20]
            },

            // Generate tables for each category
            ...generateProfessionalCategoryTables(displayResults, exportAllResults, categoryIcons),

            // Support message
            ...(supportMessage ? [
                {
                    text: 'Support Information',
                    fontSize: 20,
                    bold: true,
                    color: '#0f172a',
                    pageBreak: 'before',
                    margin: [0, 0, 0, 20]
                },
                {
                    table: {
                        widths: ['*'],
                        body: [[
                            {
                                stack: [
                                    {
                                        text: 'Contact Details',
                                        fontSize: 12,
                                        bold: true,
                                        color: '#0f172a',
                                        margin: [0, 0, 0, 10]
                                    },
                                    {
                                        text: supportMessage,
                                        fontSize: 10,
                                        color: '#475569',
                                        lineHeight: 1.5
                                    }
                                ],
                                fillColor: '#f8fafc',
                                margin: 20,
                                border: [false, false, false, false]
                            }
                        ]]
                    }
                }
            ] : [])
        ],

        styles: {
            tableHeader: {
                bold: true,
                fontSize: 10,
                color: 'white',
                fillColor: '#475569'
            }
        },

        defaultStyle: {
            fontSize: 9,
            color: '#334155'
        }
    };

    // Generate and download
    const fileName = `${clientName} - ${baselineType.includes('Standard') ? 'Standard' : 'Options'} Baseline Report.pdf`;
    pdfMake.createPdf(docDefinition).download(fileName);
}

/**
 * Calculate summary statistics from ALL results
 */
function calculateStatistics(categorizedResults) {
    let total = 0;
    let passed = 0;
    let failed = 0;

    // Safety check
    if (!categorizedResults || typeof categorizedResults !== 'object') {
        return { total: 0, passed: 0, failed: 0, passRate: '0.0' };
    }

    Object.values(categorizedResults).forEach(category => {
        if (!category || typeof category !== 'object') return;

        Object.values(category).forEach(policies => {
            if (Array.isArray(policies)) {
                policies.forEach(result => {
                    if (result && typeof result === 'object') {
                        total++;
                        if (result.Equal) {
                            passed++;
                        } else {
                            failed++;
                        }
                    }
                });
            }
        });
    });

    const passRate = total > 0 ? ((passed / total) * 100).toFixed(1) : '0.0';

    return { total, passed, failed, passRate: String(passRate) };
}

/**
 * Create enhanced statistics card with modern design
 */
function createEnhancedStatCard(label, value, primaryColor) {
    return {
        table: {
            widths: ['*'],
            heights: [70],
            body: [[
                {
                    stack: [
                        {
                            text: String(value),
                            fontSize: 28,
                            bold: true,
                            color: primaryColor,
                            alignment: 'center',
                            margin: [0, 12, 0, 4]
                        },
                        {
                            text: label,
                            fontSize: 9,
                            color: '#64748b',
                            alignment: 'center',
                            bold: true,
                            margin: [0, 0, 0, 8]
                        }
                    ],
                    fillColor: '#f8fafc',
                    border: [false, false, false, false]
                }
            ]]
        },
        layout: {
            hLineWidth: () => 2,
            vLineWidth: () => 0,
            hLineColor: () => primaryColor,
            paddingTop: () => 0,
            paddingBottom: () => 0
        }
    };
}

/**
 * Create professional category breakdown table
 */
function createProfessionalCategoryTable(categorizedResults, categoryIcons = {}) {
    const categoryStats = [];

    Object.entries(categorizedResults).forEach(([categoryName, policies]) => {
        let categoryTotal = 0;
        let categoryPassed = 0;

        Object.values(policies).forEach(results => {
            if (Array.isArray(results)) {
                results.forEach(result => {
                    categoryTotal++;
                    if (result.Equal) categoryPassed++;
                });
            }
        });

        const categoryFailed = categoryTotal - categoryPassed;
        const categoryPassRate = categoryTotal > 0
            ? ((categoryPassed / categoryTotal) * 100).toFixed(1)
            : 0;

        // Status indicator
        const statusIndicator = categoryPassRate >= 90
            ? { text: '✓', color: '#10b981', bold: true }
            : categoryPassRate >= 70
                ? { text: '⚠', color: '#f59e0b', bold: true }
                : { text: '✗', color: '#ef4444', bold: true };

        // Create category name with icon
        const categoryCell = categoryIcons[categoryName] ? {
            columns: [
                {
                    image: categoryIcons[categoryName],
                    width: 16,
                    height: 16,
                    margin: [0, 2, 0, 0]
                },
                {
                    text: categoryName,
                    bold: true,
                    color: '#1e293b',
                    margin: [5, 0, 0, 0]
                }
            ]
        } : { text: categoryName, bold: true, color: '#1e293b' };

        categoryStats.push([
            categoryCell,
            { text: categoryTotal.toString(), alignment: 'center' },
            { text: categoryPassed.toString(), color: '#059669', bold: true, alignment: 'center' },
            { text: categoryFailed.toString(), color: '#dc2626', bold: true, alignment: 'center' },
            {
                stack: [
                    {
                        text: `${categoryPassRate}%`,
                        alignment: 'center',
                        bold: true,
                        color: categoryPassRate >= 90 ? '#059669' : categoryPassRate >= 70 ? '#d97706' : '#dc2626'
                    }
                ],
                alignment: 'center'
            },
            { ...statusIndicator, alignment: 'center', fontSize: 14 }
        ]);
    });

    return {
        table: {
            headerRows: 1,
            widths: ['*', 50, 50, 50, 60, 30],
            body: [
                [
                    { text: 'Category', style: 'tableHeader' },
                    { text: 'Total', style: 'tableHeader', alignment: 'center' },
                    { text: 'Passed', style: 'tableHeader', alignment: 'center' },
                    { text: 'Failed', style: 'tableHeader', alignment: 'center' },
                    { text: 'Pass Rate', style: 'tableHeader', alignment: 'center' },
                    { text: 'Status', style: 'tableHeader', alignment: 'center' }
                ],
                ...categoryStats
            ]
        },
        layout: {
            fillColor: (rowIndex) => {
                return rowIndex === 0 ? null : (rowIndex % 2 === 0 ? '#f8fafc' : null);
            },
            hLineWidth: (i) => i === 0 || i === 1 ? 1 : 0.5,
            vLineWidth: () => 0,
            hLineColor: (i) => i === 0 || i === 1 ? '#475569' : '#e2e8f0',
            paddingLeft: () => 12,
            paddingRight: () => 12,
            paddingTop: () => 8,
            paddingBottom: () => 8
        },
        margin: [0, 0, 0, 20]
    };
}

/**
 * Generate professional detailed tables
 */
function generateProfessionalCategoryTables(categorizedResults, exportAllResults, categoryIcons = {}) {
    const tables = [];

    Object.entries(categorizedResults).forEach(([categoryName, policies], categoryIndex) => {
        // Calculate category stats for header
        let catTotal = 0, catPassed = 0;
        Object.values(policies).forEach(results => {
            if (Array.isArray(results)) {
                results.forEach(r => {
                    catTotal++;
                    if (r.Equal) catPassed++;
                });
            }
        });
        const catPassRate = catTotal > 0 ? ((catPassed / catTotal) * 100).toFixed(0) : 0;

        // Category header with icon and stats
        const headerContent = categoryIcons[categoryName] ? {
            columns: [
                {
                    image: categoryIcons[categoryName],
                    width: 18,
                    height: 18,
                    margin: [0, 2, 0, 0]
                },
                {
                    text: categoryName,
                    fontSize: 14,
                    bold: true,
                    color: 'white',
                    margin: [8, 0, 0, 0]
                }
            ],
            border: [false, false, false, false]
        } : {
            text: categoryName,
            fontSize: 14,
            bold: true,
            color: 'white',
            border: [false, false, false, false]
        };

        tables.push({
            table: {
                widths: ['*', 'auto'],
                body: [[
                    headerContent,
                    {
                        text: `${catPassed}/${catTotal} (${catPassRate}%)`,
                        fontSize: 11,
                        color: 'white',
                        alignment: 'right',
                        border: [false, false, false, false]
                    }
                ]]
            },
            layout: {
                fillColor: () => '#475569',
                paddingLeft: () => 15,
                paddingRight: () => 15,
                paddingTop: () => 10,
                paddingBottom: () => 10
            },
            margin: categoryIndex === 0 ? [0, 0, 0, 15] : [0, 25, 0, 15]
        });

        // Process each policy
        Object.entries(policies).forEach(([policyName, results]) => {
            if (!Array.isArray(results)) return;

            const filteredResults = exportAllResults
                ? results
                : results.filter(r => !r.Equal);

            if (filteredResults.length === 0) return;

            // Policy name
            tables.push({
                text: policyName,
                fontSize: 11,
                bold: true,
                color: '#1e293b',
                margin: [0, 10, 0, 8]
            });

            // Create table rows
            const tableRows = filteredResults.map(result => {
                const desiredState = formatValue(result.RefValue);
                const currentState = formatValue(result.DiffValue);

                return [
                    {
                        text: result.Path || 'N/A',
                        fontSize: 8,
                        color: '#475569'
                    },
                    {
                        text: desiredState,
                        fontSize: 8,
                        color: '#1e293b'
                    },
                    {
                        text: currentState,
                        fontSize: 8,
                        color: result.Equal ? '#059669' : '#dc2626'
                    },
                    {
                        table: {
                            widths: ['*'],
                            body: [[
                                {
                                    text: result.Equal ? 'PASS' : 'FAIL',
                                    fontSize: 8,
                                    bold: true,
                                    color: 'white',
                                    alignment: 'center',
                                    border: [false, false, false, false]
                                }
                            ]]
                        },
                        layout: {
                            fillColor: () => result.Equal ? '#10b981' : '#ef4444',
                            paddingLeft: () => 8,
                            paddingRight: () => 8,
                            paddingTop: () => 3,
                            paddingBottom: () => 3
                        }
                    }
                ];
            });

            // Add the table
            tables.push({
                table: {
                    headerRows: 1,
                    widths: ['*', 110, 110, 45],
                    body: [
                        [
                            { text: 'Setting', style: 'tableHeader', fontSize: 9 },
                            { text: 'Expected', style: 'tableHeader', fontSize: 9 },
                            { text: 'Current', style: 'tableHeader', fontSize: 9 },
                            { text: 'Status', style: 'tableHeader', fontSize: 9, alignment: 'center' }
                        ],
                        ...tableRows
                    ]
                },
                layout: {
                    fillColor: (rowIndex) => {
                        return rowIndex === 0 ? null : (rowIndex % 2 === 0 ? '#f8fafc' : null);
                    },
                    hLineWidth: (i) => i === 0 || i === 1 ? 1 : 0.5,
                    vLineWidth: () => 0,
                    hLineColor: (i) => i === 0 || i === 1 ? '#475569' : '#e2e8f0',
                    paddingLeft: () => 10,
                    paddingRight: () => 10,
                    paddingTop: () => 6,
                    paddingBottom: () => 6
                },
                margin: [0, 0, 0, 5]
            });
        });
    });

    return tables;
}

/**
 * Format value for display (truncate long values)
 */
function formatValue(value) {
    if (value === null || value === undefined) return 'Missing';
    if (value === '') return 'Empty';

    const strValue = String(value);
    if (strValue.length > 50) {
        return strValue.substring(0, 47) + '...';
    }
    return strValue;
}